import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Inbox,
  CheckCircle2,
  Clock,
  AlertCircle,
  RotateCcw,
  Check,
  X,
  User,
  Building2,
  Calendar,
  Layers,
  ChevronDown,
  ChevronUp,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { type LucideIcon } from 'lucide-react';
import { useApprovals, useReviewEntry, type PendingEntry } from '../lib/approvals';
import Shell from '../components/Shell';
import {
  EmptyState,
  TableSkeleton,
  ErrorState,
  formatDate,
  initials,
  useToast,
} from '../components/ui';

type Tab = 'pending' | 'approved' | 'rejected';

const TABS: Array<{ key: Tab; label: string; icon: LucideIcon }> = [
  { key: 'pending', label: 'Waiting for Review', icon: Clock },
  { key: 'approved', label: 'Approved Entries', icon: CheckCircle2 },
  { key: 'rejected', label: 'Returned to Site', icon: AlertCircle },
];

export default function Approvals() {
  const [tab, setTab] = useState<Tab>('pending');
  const { data, isLoading, error, refetch } = useApprovals(tab);

  return (
    <Shell title="Site Approvals & Review Inbox" crumb="Site Operations">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        <p style={{ marginTop: 0, marginBottom: 18, color: 'var(--text-soft)', fontSize: 13.5 }}>
          Site submissions and log entries require administrator verification before they are committed
          to project financial ledgers. Returning an entry sends it back to the site engineer with your explanation.
        </p>

        {/* Tab Switcher & Refresh Toolbar */}
        <div
          className="toolbar approvals-toolbar"
          style={{
            background: 'var(--surface)',
            padding: '10px 16px',
            borderRadius: 'var(--radius)',
            border: '1px solid var(--border)',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          {TABS.map((t) => {
            const IconComp = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                className={`btn btn-auto btn-sm approvals-tab-btn ${active ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setTab(t.key)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 13,
                  fontWeight: active ? 600 : 500,
                }}
              >
                <IconComp size={14} />
                <span>{t.label}</span>
                {active && data && data.length > 0 && (
                  <span
                    style={{
                      fontSize: 11,
                      padding: '1px 6px',
                      borderRadius: 'var(--radius-xs)',
                      background: 'rgba(255, 255, 255, 0.25)',
                      fontWeight: 700,
                    }}
                  >
                    {data.length}
                  </span>
                )}
              </button>
            );
          })}

          <div className="grow" />

          <button
            className="btn btn-auto btn-sm btn-ghost approvals-refresh-btn"
            onClick={() => void refetch()}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              color: 'var(--text-soft)',
              borderRadius: 'var(--radius-sm)',
            }}
          >
            <RotateCcw size={13} />
            <span>Refresh</span>
          </button>
        </div>

        {/* Entries List */}
        {isLoading ? (
          <div className="card" style={{ padding: 20 }}>
            <TableSkeleton rows={4} cols={4} />
          </div>
        ) : error ? (
          <div className="card" style={{ padding: 20 }}>
            <ErrorState error={error} onRetry={() => void refetch()} />
          </div>
        ) : !data?.length ? (
          <div className="card" style={{ padding: '40px 20px' }}>
            <EmptyState
              icon={tab === 'pending' ? 'check' : 'inbox'}
              title={tab === 'pending' ? 'Inbox is all clear!' : `No ${tab} entries found`}
              hint={
                tab === 'pending'
                  ? 'All site material purchases, labour attendance, and expense submissions have been reviewed.'
                  : 'Entries in this status category will appear here once processed.'
              }
            />
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <AnimatePresence>
              {data.map((e) => (
                <EntryCard
                  key={`${e.table}-${e.id}`}
                  entry={e}
                  reviewable={tab === 'pending'}
                />
              ))}
            </AnimatePresence>
          </div>
        )}
      </motion.div>
    </Shell>
  );
}

function EntryCard({ entry, reviewable }: { entry: PendingEntry; reviewable: boolean }) {
  const [open, setOpen] = useState(reviewable);
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState('');
  const review = useReviewEntry();
  const toast = useToast();

  async function decide(decision: 'approved' | 'rejected') {
    if (decision === 'rejected' && !note.trim()) {
      setNoteError('Please provide a reason explaining what the site engineer needs to revise');
      return;
    }
    try {
      await review.mutateAsync({ table: entry.table, id: entry.id, decision, note });
      toast.show(
        decision === 'approved'
          ? `${entry.kind} approved — committed to ${entry.projectName}`
          : `${entry.kind} returned to ${entry.submittedBy}`,
      );
    } catch (err) {
      toast.show(err instanceof Error ? err.message : 'Could not save your decision', true);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.2 }}
      className="card"
      style={{
        borderRadius: 'var(--radius)',
        border: '1px solid var(--border)',
        background: 'var(--surface)',
        overflow: 'hidden',
      }}
    >
      {/* Card Header Summary */}
      <div
        className="approval-card-head"
        style={{
          padding: '16px 20px',
          display: 'flex',
          gap: 16,
          alignItems: 'center',
          cursor: 'pointer',
          background: open ? 'var(--surface-muted)' : 'var(--surface)',
          borderBottom: open ? '1px solid var(--border)' : '1px solid transparent',
          transition: 'all 0.15s ease',
        }}
        onClick={() => setOpen((v) => !v)}
      >
        <div
          className="avatar-sq approval-avatar"
          style={{
            width: 44,
            height: 44,
            fontSize: 14,
            fontWeight: 700,
            borderRadius: 'var(--radius-sm)',
            background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.1), rgba(37, 99, 235, 0.02))',
            color: 'var(--brand)',
            border: '1px solid rgba(37, 99, 235, 0.2)',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {initials(entry.submittedBy)}
        </div>

        <div className="approval-main-info" style={{ minWidth: 0, flex: 1 }}>
          <div className="approval-title-wrap" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span
              className="approval-kind"
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: 'var(--text)',
              }}
            >
              {entry.kind}
            </span>
            <span className="approval-dot" style={{ color: 'var(--text-faint)' }}>•</span>
            <span className="approval-summary-desktop" style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-soft)' }}>
              {entry.summary}
            </span>
          </div>

          <div className="approval-summary-mobile">
            {entry.summary}
          </div>

          <div
            className="approval-meta-row"
            style={{
              display: 'flex',
              gap: 14,
              flexWrap: 'wrap',
              marginTop: 4,
              fontSize: 12.5,
              color: 'var(--text-soft)',
              alignItems: 'center',
            }}
          >
            <span className="approval-meta-item" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 500 }}>
              <Building2 size={12} style={{ color: 'var(--text-faint)' }} />
              <span>{entry.projectName}</span>
            </span>

            <span className="approval-meta-item" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <User size={12} style={{ color: 'var(--text-faint)' }} />
              <span>Sent by {entry.submittedBy}</span>
            </span>

            <span className="approval-meta-item" style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Calendar size={12} style={{ color: 'var(--text-faint)' }} />
              <span>{formatDate(entry.date)}</span>
            </span>
          </div>
        </div>

        <div className="approval-status-group" style={{ display: 'flex', gap: 10, alignItems: 'center', flexShrink: 0 }}>
          {entry.status === 'approved' && (
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                padding: '3px 9px',
                borderRadius: 'var(--radius-xs)',
                background: 'rgba(22, 163, 74, 0.1)',
                color: '#16a34a',
                border: '1px solid rgba(22, 163, 74, 0.2)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <CheckCircle2 size={12} />
              <span>Approved</span>
            </span>
          )}
          {entry.status === 'rejected' && (
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                padding: '3px 9px',
                borderRadius: 'var(--radius-xs)',
                background: 'rgba(239, 68, 68, 0.1)',
                color: '#dc2626',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <AlertCircle size={12} />
              <span>Returned</span>
            </span>
          )}
          {entry.status === 'pending' && (
            <span
              style={{
                fontSize: 11.5,
                fontWeight: 600,
                padding: '3px 9px',
                borderRadius: 'var(--radius-xs)',
                background: 'rgba(234, 88, 12, 0.1)',
                color: '#ea580c',
                border: '1px solid rgba(234, 88, 12, 0.2)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Clock size={12} />
              <span>Waiting Review</span>
            </span>
          )}

          {open ? <ChevronUp size={16} style={{ color: 'var(--text-faint)' }} /> : <ChevronDown size={16} style={{ color: 'var(--text-faint)' }} />}
        </div>
      </div>

      {/* Card Body Details */}
      {open && (
        <div style={{ padding: '20px 24px', background: 'var(--surface)' }}>
          <div
            className="approval-detail-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '12px 20px',
              fontSize: 13,
              marginBottom: reviewable ? 18 : 0,
              background: 'var(--surface-muted)',
              padding: '14px 18px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
            }}
          >
            {entry.detail.map(([k, v]) => (
              <div key={k} style={{ minWidth: 0 }}>
                <div style={{ fontSize: 11.5, color: 'var(--text-faint)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  {k}
                </div>
                <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text)', marginTop: 2 }}>
                  {v}
                </div>
              </div>
            ))}
          </div>

          {entry.reviewNote && !reviewable && (
            <div
              style={{
                padding: '10px 14px',
                borderRadius: 'var(--radius-sm)',
                fontSize: 13,
                marginTop: 14,
                background: 'rgba(239, 68, 68, 0.08)',
                color: '#dc2626',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
              }}
            >
              <AlertTriangle size={15} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <strong>Reason given for return:</strong> {entry.reviewNote}
              </div>
            </div>
          )}

          {reviewable && (
            <div style={{ marginTop: 16 }}>
              <div className="field" style={{ marginBottom: 14 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>Return Explanation / Revision Note</span>
                  <span className="opt" style={{ color: 'var(--text-faint)', fontSize: 11.5 }}>
                    (Required only if returning to site)
                  </span>
                </label>
                <input
                  value={note}
                  aria-invalid={!!noteError}
                  onChange={(e) => {
                    setNote(e.target.value);
                    setNoteError('');
                  }}
                  style={{
                    borderRadius: 'var(--radius-sm)',
                    border: noteError ? '1px solid var(--red)' : '1px solid var(--border)',
                    height: 38,
                    fontSize: 13,
                  }}
                />
                {noteError && (
                  <div className="err" style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>
                    {noteError}
                  </div>
                )}
              </div>

              <div className="approval-actions" style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-auto btn-sm btn-danger"
                  disabled={review.isPending}
                  onClick={() => void decide('rejected')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    borderRadius: 'var(--radius-sm)',
                  }}
                >
                  <X size={14} />
                  <span>Return to Site</span>
                </button>

                <button
                  className="btn btn-auto btn-sm btn-primary"
                  disabled={review.isPending}
                  onClick={() => void decide('approved')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    borderRadius: 'var(--radius-sm)',
                  }}
                >
                  <Check size={14} />
                  <span>{review.isPending ? 'Committing…' : 'Approve Submission'}</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
